import React from 'react';
import Rollbar from 'rollbar';

type Props = {
  errorPage: React.ReactNode;
  loadingScreenErrorPage: React.ReactNode;
  children: React.ReactNode;
  user?: {
    id?: number;
    slug?: string;
    email?: string;
  };
};

type State = {
  hasError: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
  state = { hasError: false, error: null };
  user = this.props.user;

  readonly rollbarConfig = {
    accessToken: process.env.ROLLBAR_JS_API_KEY,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
      environment: process.env.NODE_ENV,
      client: {
        javascript: {
          code_version: process.env.SOURCE_VERSION,
          source_map_enabled: true,
        },
      },
      person: this.user
        ? {
            id: this.user.id, // to match rollbar_js_embed_code in application_helper.rb, but should be deprecated
            email: this.user.email, // to match rollbar_js_embed_code in application_helper.rb
            slug: this.user.slug,
          }
        : null,
    },
  };

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    // Get the error message and put it in the state
    const errorMessage = error && error.message ? error.message : error;
    const safeErrorMessage = typeof errorMessage === 'string' || errorMessage instanceof String ? errorMessage : null;
    return { hasError: true, error: safeErrorMessage };
  }

  componentDidMount() {
    (window as any).callErrorPage = () => {
      this.setState({ hasError: true });
    };
  }

  componentDidCatch(error, errorInfo) {
    const rollbar = new Rollbar(this.rollbarConfig);
    if (rollbar) rollbar.critical(error);
    const segment = (window as any).analytics;
    if (segment) segment.track('Encountered Error', { error, errorInfo }, { integrations: { Calixa: false } });
  }

  render() {
    if (this.state.hasError) {
      // This logic detects if the error that was thrown is a chunk loading error
      // and if so, takes the user to a fake loading screen that refreshes the page
      // we are not sure why the chunk loading error is occurring but refreshing the page seems to fix it
      // see JIRA ticket for details: https://workpatterns.atlassian.net/browse/WP-1347
      if (
        this.state.error &&
        this.state.error.startsWith('ChunkLoadError: Loading chunk ') &&
        this.state.error.includes(' failed.')
      )
        return this.props.loadingScreenErrorPage;
      return this.props.errorPage;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
